import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { TensionLightingSection } from "@/components/TensionLightingSection";
import hero from "@/assets/sail-shade-ballfield-blue-canopies.jpg";
import heroPicture from "@/assets/sail-shade-ballfield-blue-canopies.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import tennis from "@/assets/school-tennis-court.jpg";
import goalposts from "@/assets/school-goalposts.jpg";
import sailShade from "@/assets/sail-shade-blue-underside.jpg";
import wyatt from "@/assets/wyatt-banner-la-vernia.jpg";
import playground from "@/assets/playground-rope-climber-padded-anchors-serge-ferrari.jpg";

export const Route = createFileRoute("/school-shade-structures")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "School Shade Structures — Playground, Cafeteria & Athletic Shade Sails | Texas Made Shade",
      description:
        "Custom shade structures for Texas school districts — playground sails, cafeteria patio canopies, athletic field shade and bus-loop covers.",
      image: hero,
      imageAlt: "Blue tensioned sail-shade canopies installed over a Texas school athletic field",
      socialTitle: "School Shade Structures — Texas Made Shade",
      socialDescription:
        "Playground, cafeteria, athletic and bus-loop shade structures for Texas school districts and ISDs.",
      keywords:
        "school shade structures, playground shade sail, school cafeteria patio shade, athletic field shade structure, ISD shade sail Texas, bus loop shade canopy, school shade Texas, district playground shade",
    }),
    scripts: serviceHeadScripts({
      path: "/school-shade-structures",
      serviceName: "School Shade Structures",
      breadcrumbName: "School Shade Structures",
      description:
        "Engineered shade structures for Texas school districts — playground sails, cafeteria patios, athletic fields and bus-loop covers.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/school-shade-structures"
      serviceName="School Shade Structures"
      breadcrumbs={[{ label: "School Shade Structures" }]}
      whyUsPlacement="afterHero"
      eyebrow="★ SCHOOL SHADE STRUCTURES ★"
      title="School Shade Structures — Playgrounds, Cafeterias, Athletic Fields"
      intro="Texas Made Shade designs and builds shade structures for ISDs, charters and universities across Texas — playground sails, cafeteria patio canopies, athletic field shade and bus-loop covers. Engineered posts, welded brackets, UV-stable Serge Ferrari® and Phifertex® shade fabrics in school colors, fabricated to district spec in our Seguin shop."
      heroImage={heroPicture}
      features={[
        "Playground shade sails sized to standard play structures",
        "Cafeteria patio and outdoor classroom canopies",
        "Athletic field, bleacher and dugout shade",
        "Bus-loop and student-pickup line canopies",
        "Engineered welded posts and brackets — district-spec",
        "Serge Ferrari® and Phifertex® UV-stable shade fabrics",
      ]}
      body={[
        { heading: "Playground Shade Sails", text: "Tensioned shade sails over playground equipment cut surface temps by up to 30°F and protect kids from UV — sized to your existing play structures and rated for Texas wind loads." },
        { heading: "Cafeteria Patios & Outdoor Classrooms", text: "Permanent shade over outdoor lunch tables and classroom decks — engineered welded posts with cable-tensioned UV fabric, designed to drop into your district's facility plan." },
        { heading: "Athletic Field & Bleacher Shade", text: "Shade canopies over bleachers, dugouts, batting cages and team areas keep athletes and parents out of full Texas sun.", image: { src: sailShade, alt: "Underside of a tensioned blue Sunbrella sail shade fabricated by Texas Made Shade" } },
        { heading: "Bus Loops & Student Pickup", text: "Cantilever and post-supported shade structures over bus loading zones and parent-pickup lines — protect students and staff at peak sun." },
        { heading: "Branded School Canopies", text: "Welded aluminum entry canopies and storefront banners in school colors and lettering for athletic facilities, booster buildings and admin entries.", image: { src: wyatt, alt: "Custom commercial banner awning at La Vernia ISD area storefront — same build used for branded school canopies" } },
        { heading: "Playground Hardware Padding", text: "Custom Serge Ferrari® padded covers for rope-climber anchors, web structure hardware and impact zones — district-spec safety with school colors.", image: { src: playground, alt: "Custom teal Serge Ferrari padded anchor covers on playground rope-climber web structure" } },
      ]}
      applicationsEyebrow="WHERE WE INSTALL"
      applicationsHeading="School Shade Applications"
      applications={[
        "Playgrounds",
        "Cafeteria Patios",
        "Outdoor Classrooms",
        "Athletic Fields",
        "Bleachers & Dugouts",
        "Bus Loops",
        "Student Pickup Lines",
        "Tennis & Track Complexes",
        "Pool Decks",
        "Booster & Admin Entries",
      ]}
      faqs={[
        { q: "Do you build shade structures for Texas school districts and ISDs?", a: "Yes — we build engineered shade structures for ISDs, charter schools, private schools and universities across Texas. We work directly with facility managers and district purchasing on PO-ready quotes and district-spec fabrication." },
        { q: "How much does a school playground shade sail cost?", a: "Cost depends on size, fabric, and post engineering, but most playground sails for Texas schools run between $8K–$35K installed. Send dimensions and a site photo for a free written quote." },
        { q: "Are your school shade structures rated for Texas wind?", a: "Yes — every engineered shade post and bracket assembly is designed for the local wind load of the install site, with welded steel posts and stainless cable hardware." },
        { q: "Can you match our school colors and add lettering?", a: "Absolutely. UV shade fabric is available in a wide range of school colors and we can add hand-painted or printed lettering on adjacent entry canopies and banners." },
      ]}
      gallerySection={{
        eyebrow: "School Work",
        title: "Recent School & Athletic Shade Installs",
        images: [
          { src: hero, alt: "Tensioned blue sail-shade canopies installed over a Texas school athletic field" },
          { src: tennis, alt: "Custom tennis court windscreens at a Texas high school" },
          { src: goalposts, alt: "Padded football goalpost covers at a Texas school stadium" },
          { src: playground, alt: "Custom Serge Ferrari padded anchor covers on a playground rope-climber" },
          { src: sailShade, alt: "Underside of tensioned blue sail shade — same build used over playgrounds" },
          { src: wyatt, alt: "Branded school district storefront awning by Texas Made Shade" },
        ],
      }}
    >
    <TensionLightingSection />
    </ServicePage>
  ),
});
